import React, { useContext, useMemo, useEffect, useState } from 'react'
import _ from 'lodash'

import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react'
import { BreakpointTrackerContext } from '@jutro/layout'
import { WizardPage } from '@xengage/gw-portals-wizard-react'
import { useValidation } from '@xengage/gw-portals-validation-react'
import { TranslatorContext } from '@jutro/locale';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useModal } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import UWCompanyUtil from '../../utils/UWCompanyUtil_TWIA.js';
import AgentRegistrationService from '../../../../capabilities/ent-capability-agentregistration/services/AgentRegistrationService.js'
import metadata from './LicenseDetailsPage.metadata.json5'
import messages from './LicenseDetailsPage.messages.js'
import styles from './LicenseDetailsPage.module.scss'

function LicenseDetailsPage(props) {
    const { wizardData: searchVM, updateWizardData, id, steps, changeNextSteps } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const { onValidate, isComponentValid } = useValidation(id);
    //const { authHeader } = useAuthentication();
    const { showAlert } = useModal();

    const queryString = require('query-string')
    const parsedParams = queryString.parse(location.search)

    const createRegistrationVM = () => {
        const clarification = parsedParams['clarification'];
        const newRegistration = {
        };
        if (clarification) {
          newRegistration.isAgencyVerification = true;
          // C-type is clarification type, s for SSN and e for EIN
          // This will show the appropriate widgets.
          if (parsedParams['cType'] === 's') {
            _.set(newRegistration, 'isSSN', true)
          } else if (parsedParams['cType'] === 'e') {
            _.set(newRegistration, 'isSSN', false)
          }
        }

        const registrationModel = viewModelService.create(newRegistration, 'pc', 'org.tfpa.edge.capabilities.agent.dto.TDIAgencySearchDTO')

        const uwCompanyCode = parsedParams['company'];
        if (uwCompanyCode) {
            const termTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('UWCompanyCode')
            if (termTypeList.hasCode(uwCompanyCode)) {
                const termTypeCode = termTypeList.getCode(uwCompanyCode);  
                _.set(registrationModel, 'uwcompany.value', termTypeCode)
            }
            // US26498 update wizard steps if company is loaded from URL
            handleChangeSteps(uwCompanyCode);
        }

        return registrationModel
     }
    // DE15243 - To stop the swap between UW Company
    const company = _.get(searchVM, 'uwcompany.value', undefined);
    
    useEffect(() => {
        if(viewModelService) {
            const registrationModel = createRegistrationVM();

            // US26498 don't set an empty company value
            if (company) {
                _.set(registrationModel, 'uwcompany.value', company);
            }
            updateWizardData(registrationModel);
        }
    }, [viewModelService])

     const getCarrierName = (code) => {
        if(code.code == '2001_00001') return code.name
        if(code.code == '1001_00001') return messages.TFPA.id
     }

    const getUWCarrierTypeList = useMemo(() => {
        const UWCarrierTypelist = viewModelService?.productMetadata?.get('pc').types.getTypelist('UWCompanyCode');
        const formattedTypes =  UWCarrierTypelist?.codes.filter(code => code.code !== "default").map((code) => {
            const formatedCode = {
                code: code.code,
                name: translator({id: getCarrierName(code)})
            }
            return formatedCode
        })
        return formattedTypes
    },[viewModelService]);

    const handleChangeSteps = uwCompany => {
        try {
            const uwCode = viewModelService?.productMetadata?.get('pc').types?.getTypelist('UWCompanyCode')?.getCode(uwCompany);
            const newSteps = steps.filter(step => ((step.stepProps['company'] == uwCompany || step.stepProps['company'] == undefined) && step.id !== 'LicenseDetails'))
            changeNextSteps(newSteps)
            _.set(searchVM, 'uwcompany', uwCode)
            updateWizardData(searchVM)
        } catch(e) {
            // US26498 bad data might come from URL
            console.error(`unable to handle step change ${e}`);
        }
    }

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        licenseNumber: {
            visible: !_.isUndefined(_.get(searchVM, 'uwcompany.value'))
        },
        uWCompany: {
            visible: _.isUndefined(_.get(searchVM, 'uwcompany.value')),
            availableValues: getUWCarrierTypeList,
            onValueChange: handleChangeSteps
        },
        isAgencyVerificationContainer: {
            visible: !_.isUndefined(_.get(searchVM, 'isAgencyVerification.value'))
        },
        taxIDSSN: {
            visible: _.get(searchVM, 'isSSN.value') === true
        },
        taxIDEIN: {
            visible: _.get(searchVM, 'isSSN.value') === false
        },
        agencyContactLastName: {
            visible: !_.isUndefined(_.get(searchVM, 'isAgencyVerification.value'))
        }
    }

    const resolvers = {
        resolveClassNameMap: styles
    }

    /**
     * DE16830 title messages for error handling modals
     */ 
    const handleErrorCodeTitle = errorCode => {

        if (errorCode) {
            switch (errorCode) {
                case 5160:
                    return messages.RegistrationNotFoundTitle;
                default:
                    return messages.error;
            }
        }
    }

    const handleErrorCode = (errorCode, companyCode) => {
        const isTWIACompany = UWCompanyUtil.isTWIACompany({value:{code:companyCode}});

        if (errorCode) {
            switch (errorCode) {
              case 5100:
                  return messages.agencyNotFound
              case 5120:
                return messages.invalidLicense
              case 5130:
                return messages.NotGLPCLicense
              case 5140:
                if (isTWIACompany) {
                    return messages.TWIALicenseInUse
                } else {
                    return messages.LicenseInUse
                }
              case 5150:
                return messages.EmailInUse
              case 5160:
                return messages.RegistrationNotFoundBody // DE16830
              case 5170:
                return messages.RegistrationComplete
              case 5180:
                return messages.InvalidLicenseUpdate
              case 5110:
                if (isTWIACompany) {
                    return messages.TWIAExpiredLicense
                } else {
                    return messages.ExpiredLicense
                }
              default:
                return messages.UnableToValidate
            }
          }
        }

    const onNext = () => {
        if (!isComponentValid) {
            setShowErrors(true)
            return false
        }

        // Make a call searchByLicenseNumber to get Agency details. 
        
        return AgentRegistrationService.searchByLicenseNumber(
            searchVM.value
        ).then(response => {

            if (!response.agencyContact) {
                _.set(response, 'agencyContact', {})
                _.set(response, 'hasMailingAddress', false)
                _.set(response, 'taxInfo', {})
                _.set(response, 'taxAddress.country', 'US')
                _.set(response, 'agentEFTDTO_TWIA.mailingAddress.country', 'US')
            }
            // DE16834 E&O unselected on clarification 
            if (!response.eoinsuranceInfo) {
                _.set(response, 'eoinsuranceInfo.hasEOCoverage', false)
            }
            if (!response.mailingAddress) {
                _.set(response, 'mailingAddress.country', 'US')
            }
            if (!response.directDepositInfo) {
                _.set(response, 'directDepositInfo.isCommercialAccount', false)
            }
            const registrationModel = viewModelService.create(response, 'pc', 'org.tfpa.edge.capabilities.agent.dto.AgentRegistrationDTO')
            // US26498 set EFT address is it's not already available
            if (_.get(searchVM, 'isAgencyVerification.value') === true && _.get(registrationModel, 'agentEFTDTO_TWIA.value') === undefined) {
                registrationModel.agentEFTDTO_TWIA.value = {};
                registrationModel.agentEFTDTO_TWIA.mailingAddress.value = {};
                const tdiAddressLookup = _.get(registrationModel, 'tdiaddressLookup.value');
                const tdiAddresses = _.get(registrationModel, 'agencyAddresses.value');
                const tdiAddress = tdiAddresses && tdiAddresses.find(address => address.recordID == tdiAddressLookup);
                if (tdiAddress) {
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.displayName', tdiAddress.displayName);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.addressLine1', tdiAddress.addressLine1);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.addressLine2', tdiAddress.addressLine2);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.addressLine3', tdiAddress.addressLine3);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.city', tdiAddress.city);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.postalCode', tdiAddress.postalCode);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.state', tdiAddress.state);
                    _.set(registrationModel, 'agentEFTDTO_TWIA.mailingAddress.country', tdiAddress.country);
                }
            }
            return registrationModel;
        }).catch(e => {
            const errorCode = _.get(e, 'data.appErrorCode')
            const companyCode = _.get(e, 'gwInfo.params[0].uwcompany')
        
            showAlert({
                title: handleErrorCodeTitle(errorCode), // DE16830
                message: handleErrorCode(errorCode, companyCode),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop);
            return false
        })
    }

    return (
        <WizardPage
            id={id}
            showPrevious={false}
            showNext={!_.isUndefined(_.get(searchVM, 'uwcompany.value'))}
            showCancel={!_.isUndefined(_.get(searchVM, 'uwcompany.value'))}
            onNext={onNext}
            disableCancel={!_.get(searchVM, 'licenseNumber.value')}
        >
            <ViewModelForm 
                uiProps={metadata.pageContent}
                model={searchVM}
                onModelChange={updateWizardData}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
        </WizardPage>
    )

}

export default LicenseDetailsPage;

